// Lazy-load components only when they're present on the page.
// Each component is gated by a DOM selector check to avoid
// downloading JS for components not rendered by Liquid.

import Layout from 'utils/layout';
import Video from 'utils/video';

const layout = Layout.getInstance();

/**
 * Initialize a data-section component with the standard contract.
 * @param {string} name - The data-section attribute value
 * @param {Function} importFn - Function returning import() promise
 */
function initSection(name, importFn) {
  const elements = document.querySelectorAll(`[data-section="${name}"]`);
  if (!elements.length) return;

  importFn()
    .then((m) => {
      elements.forEach((element) => {
        const dataEl = element.querySelector('[data-section-data]');
        let data = {};
        if (dataEl) {
          try {
            data = JSON.parse(dataEl.textContent);
          } catch (e) {
            console.error(`[${name}] Failed to parse section data`, e);
          }
        }
        const videoEls = element.querySelectorAll('[data-component="video"]');
        const videos = videoEls.length
          ? [...videoEls].map((videoEl) => new Video({ element, video: videoEl, layout }))
          : [];
        m.default(element, { ...data, layout, videos });
      });
    })
    .catch(console.error);
}

// -- Top-level components (27) -----------------------------------------------
// Note: symbolism has data-section in Liquid but no component JS (pre-existing,
// same situation as product-bundle). Omitted intentionally.
initSection('announcement-bar', () => import('./announcement-bar.js'));
initSection('article', () => import('./article.js'));
initSection('builder', () => import('./builder.js'));
initSection('cart', () => import('./cart.js'));
initSection('cart-item', () => import('./cart-item.js'));
initSection('collection-top-slider', () => import('./collection-top-slider.js'));
initSection('contact-form', () => import('./contact-form.js'));
initSection('dotted-products', () => import('./dotted-products.js'));
initSection('example', () => import('./example.js'));
initSection('feature-tabs', () => import('./feature-tabs.js'));
initSection('featured-collection-slider', () => import('./featured-collection-slider.js'));
initSection('featured-image-text', () => import('./featured-image-text.js'));
initSection('footer', () => import('./footer.js'));
initSection('header', () => import('./header.js'));
initSection('marquee', () => import('./marquee.js'));
initSection('merch-highlights', () => import('./merch-highlights.js'));
initSection('predictive-search', () => import('./predictive-search.js'));
initSection('product', () => import('./product.js'));
initSection('product-form', () => import('./product-form.js'));
initSection('product-grid', () => import('./product-grid.js'));
initSection('product-slider', () => import('./product-slider.js'));
initSection('product-variants-block', () => import('./product-variants-block.js'));
initSection('products-carousel', () => import('./products-carousel.js'));
initSection('swatch-picker', () => import('./swatch-picker.js'));
initSection('text-with-navigation', () => import('./text-with-navigation.js'));
initSection('wishlist', () => import('./wishlist.js'));
initSection('wishlist-modal', () => import('./wishlist-modal.js'));

// -- Customer components (4) -------------------------------------------------
initSection('customer/login', () => import('./customer/login.js'));
initSection('customer/register', () => import('./customer/register.js'));
initSection('customer/addresses', () => import('./customer/addresses.js'));
initSection('customer/reset_password', () => import('./customer/reset_password.js'));

// -- Custom Element components (no data-section contract) --------------------
if (document.querySelector('search-form')) {
  import('./search-form.js').then((m) => m.default()).catch(console.error);
}
